import { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { CaretRight, ClockCountdown, Warning, Info, PencilSimple } from "@phosphor-icons/react";
import ClienteSapInput from "../components/ClienteSapInput";

const getMeses12 = () => {
  const meses = [];
  const now = new Date();
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = `${cap(d.toLocaleDateString("pt-BR", { month: "long" }))}/${d.getFullYear()}`;
    meses.push({ val, label });
  }
  return meses;
};

const calcDdfInfo = (mesEmbarque, dias) => {
  if (!mesEmbarque || !dias || isNaN(Number(dias)) || Number(dias) <= 0) return null;
  const [ano, mes] = mesEmbarque.split("-").map(Number);
  const dataPagamento = new Date(ano, mes - 1, 1);
  dataPagamento.setDate(dataPagamento.getDate() + Number(dias));
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const prazo = Math.ceil((dataPagamento - hoje) / (1000 * 60 * 60 * 24));
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const mesEmbarqueLabel = `${cap(new Date(ano, mes - 1, 1).toLocaleDateString("pt-BR", { month: "long" }))}/${ano}`;
  const mesPagLabel = `${cap(dataPagamento.toLocaleDateString("pt-BR", { month: "long" }))}/${dataPagamento.getFullYear()}`;
  return { prazo, mesEmbarqueLabel, mesPagLabel };
};

export default function HedgeManual() {
  const [quote, setQuote] = useState(null);
  const [flash, setFlash] = useState("");
  const [form, setForm] = useState({ cliente: "", volume_usd: "", data_vencimento: "" });
  const [volumeDisplay, setVolumeDisplay] = useState("");
  const [loading, setLoading] = useState(false);
  const [clienteSap, setClienteSap] = useState(null);
  const [cnpjIeConfirmed, setCnpjIeConfirmed] = useState(null);
  // Ver Vendedor.js: remontar o ClienteSapInput é o que limpa o estado
  // interno dele (query, resultados, modo CNPJ/IE).
  const [clienteKey, setClienteKey] = useState(0);
  const [confirmation, setConfirmation] = useState(null);
  const timerRef = useRef(null);
  const [tipoVencimento, setTipoVencimento] = useState("data_fixa");
  const [ddfMes, setDdfMes] = useState("");
  const [ddfDias, setDdfDias] = useState("");
  const [lastUpdate, setLastUpdate] = useState(null);
  const [updating, setUpdating] = useState(false);

  const [spotManual, setSpotManual] = useState("");
  const [spotManualDisplay, setSpotManualDisplay] = useState("");

  const spotManualNum = parseFloat(spotManual) || 0;

  const loadQuoteWithTime = async () => {
    setUpdating(true);
    try {
      const { data } = await api.get("/cotacao/spot");
      setQuote((old) => {
        if (old && data.spot !== old.spot) {
          setFlash(data.spot > old.spot ? "flash-up" : "flash-down");
          setTimeout(() => setFlash(""), 700);
        }
        return data;
      });
      setLastUpdate(new Date());
      setSpotManual((prev) => {
        if (!prev && data.spot) {
          setSpotManualDisplay(data.spot.toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 }));
          return String(data.spot);
        }
        return prev;
      });
    } catch (e) {
      console.error(e);
    } finally {
      setTimeout(() => setUpdating(false), 500);
    }
  };

  useEffect(() => {
    loadQuoteWithTime();
    timerRef.current = setInterval(loadQuoteWithTime, 40000);
    return () => clearInterval(timerRef.current);
  }, []);

  const handleClienteChange = ({ cliente, clienteSap: sap, manualId }) => {
    setClienteSap(sap);
    setCnpjIeConfirmed(manualId);
    setForm(f => ({ ...f, cliente }));
  };

  const handleSpotManualChange = (e) => {
    const raw = e.target.value.replace(/\./g, "").replace(/[^\d,]/g, "");
    if (!raw) { setSpotManualDisplay(""); setSpotManual(""); return; }
    const comma = raw.indexOf(",");
    if (comma === -1) {
      const n = parseInt(raw, 10);
      setSpotManualDisplay(isNaN(n) ? "" : n.toLocaleString("pt-BR"));
      setSpotManual(isNaN(n) ? "" : String(n));
    } else {
      const intStr = raw.slice(0, comma);
      const decStr = raw.slice(comma + 1).replace(/,/g, "").slice(0, 4);
      const n = intStr ? parseInt(intStr, 10) : 0;
      setSpotManualDisplay(`${intStr ? n.toLocaleString("pt-BR") : "0"},${decStr}`);
      setSpotManual(`${intStr || "0"}.${decStr}`);
    }
  };

  const resetForm = () => {
    setForm({ cliente: "", volume_usd: "", data_vencimento: "" });
    setVolumeDisplay("");
    setDdfMes("");
    setDdfDias("");
    setTipoVencimento("data_fixa");
    setClienteSap(null);
    setCnpjIeConfirmed(null);
    setClienteKey(k => k + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.cliente || !form.volume_usd) {
      toast.error("Preencha todos os campos");
      return;
    }
    if (!spotManualNum || spotManualNum <= 0) {
      toast.error("Informe o dólar do momento para calcular a trava");
      return;
    }
    if (tipoVencimento === "data_fixa" && !form.data_vencimento) {
      toast.error("Preencha a data de vencimento");
      return;
    }
    if (tipoVencimento === "ddf" && (!ddfMes || !ddfDias)) {
      toast.error("Preencha o mês de embarque e os dias DDF");
      return;
    }
    setLoading(true);
    try {
      const sapBp = clienteSap?.business_partner || null;
      const sapBps = clienteSap?.business_partners || null;
      let payload;
      if (tipoVencimento === "data_fixa") {
        payload = {
          cliente: form.cliente,
          volume_usd: Number(form.volume_usd),
          data_vencimento: form.data_vencimento,
          tipo_vencimento: "data_fixa",
          sap_business_partner: sapBp,
          business_partners: sapBps,
          spot_manual: spotManualNum,
        };
      } else {
        const info = calcDdfInfo(ddfMes, ddfDias);
        if (!info || info.prazo <= 0) {
          toast.error("Prazo DDF inválido");
          setLoading(false);
          return;
        }
        payload = {
          cliente: form.cliente,
          volume_usd: Number(form.volume_usd),
          tipo_vencimento: "ddf",
          ddf_dias: Number(ddfDias),
          ddf_mes_embarque: ddfMes,
          prazo_dias: info.prazo,
          sap_business_partner: sapBp,
          business_partners: sapBps,
          spot_manual: spotManualNum,
        };
      }
      const { data } = await api.post("/travas/manual", payload);
      setConfirmation(data);
      toast.success(`Trava ${data.codigo} criada`);
      resetForm();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar trava manual");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n, dec = 4) => n?.toLocaleString("pt-BR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

  if (confirmation) {
    return (
      <Layout>
        <div className="p-8 max-w-2xl">
          <div className="border border-[#FFB800] bg-[#FFB800]/5 rounded-sm p-8 space-y-6">
            <div className="flex items-center gap-3">
              <ClockCountdown weight="fill" className="w-8 h-8 text-[#FFB800]" />
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#FFB800]">Aguardando Aprovação</p>
                <h2 className="text-2xl font-bold font-mono-data">{confirmation.codigo}</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><p className="text-xs text-[#6b89a8] uppercase tracking-wider">Cliente</p><p className="font-medium">{confirmation.cliente}</p></div>
              <div><p className="text-xs text-[#6b89a8] uppercase tracking-wider">Volume</p><p className="font-mono-data">USD {fmt(confirmation.volume_usd, 2)}</p></div>
              <div>
                <p className="text-xs text-[#6b89a8] uppercase tracking-wider">Taxa travada</p>
                <p className="font-mono-data text-[#40B6A2] text-lg">R$ {fmt(confirmation.taxa_final)}</p>
              </div>
              <div>
                <p className="text-xs text-[#6b89a8] uppercase tracking-wider">Prazo</p>
                {confirmation.tipo_vencimento === "ddf" && confirmation.ddf_mes_embarque ? (
                  <p className="font-mono-data text-sm">
                    {(() => {
                      const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
                      const [ano, mes] = confirmation.ddf_mes_embarque.split("-").map(Number);
                      const embarqueLabel = `${cap(new Date(ano, mes - 1, 1).toLocaleDateString("pt-BR", { month: "long" }))}/${ano}`;
                      const pagDate = new Date(ano, mes - 1, 1);
                      pagDate.setDate(pagDate.getDate() + (confirmation.ddf_dias || 0));
                      const pagLabel = `${cap(pagDate.toLocaleDateString("pt-BR", { month: "long" }))}/${pagDate.getFullYear()}`;
                      return `DDF · ${confirmation.ddf_dias} dias · Embarque ${embarqueLabel} · Pagamento est. ${pagLabel}`;
                    })()}
                  </p>
                ) : (
                  <p className="font-mono-data">{confirmation.prazo_dias} dias</p>
                )}
              </div>
              <div>
                <p className="text-xs text-[#6b89a8] uppercase tracking-wider">Status</p>
                <Badge className="bg-[#FFB800]/10 text-[#FFB800] border border-[#FFB800]/30 font-mono-data text-[10px] tracking-wider uppercase">
                  Pend. vinculação
                </Badge>
              </div>
            </div>
            <div className="border-t border-[#123a5e] pt-4 flex items-start gap-2 text-xs text-[#8aa9c9]">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#FFB800]" />
              <span>A trava está pendente de vinculação ao pedido do Sysfértil.</span>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => setConfirmation(null)} className="bg-[#40B6A2] text-black hover:bg-[#359a89]">
                Nova trava manual
              </Button>
              <Button variant="outline" onClick={() => window.location.href = "/hedge/travas"}>
                Ver travas
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6 max-w-5xl">
        <div>
          <p className="text-[11px] tracking-[0.25em] text-[#6b89a8] uppercase">Hedge · Cotação Manual</p>
          <h1 className="text-3xl font-bold tracking-tight">Trava Manual</h1>
        </div>

        {/* Card spot de referência + campo dólar manual */}
        <div className="border border-[#123a5e] rounded-sm p-6 bg-[#0B2D4D] space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="live-dot opacity-60" />
              <p className="text-xs tracking-[0.2em] uppercase text-[#8aa9c9]">USD / BRL · Referência ao vivo</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#6b89a8] font-mono-data">
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${updating ? "bg-[#FFB800] animate-pulse" : "bg-[#40B6A2]"}`} />
              <span>{lastUpdate ? lastUpdate.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}</span>
            </div>
          </div>

          <div className="flex items-baseline gap-4">
            <p className={`text-4xl font-bold font-mono-data text-[#6b89a8] ${flash}`}>
              {quote?.spot ? fmt(quote.spot) : "—"}
            </p>
            <span className="text-xs text-[#6b89a8] uppercase tracking-widest">spot de referência</span>
          </div>

          {/* Campo do dólar manual */}
          <div className="border-t border-[#123a5e] pt-5 space-y-2">
            <div className="flex items-center gap-2">
              <PencilSimple className="w-4 h-4 text-[#FFB800]" />
              <Label className="text-xs tracking-wider uppercase text-[#FFB800] font-semibold">
                Dólar Travado (Manual)
              </Label>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-[#8aa9c9] font-mono-data">R$</span>
              <Input
                data-testid="input-spot-manual"
                type="text"
                inputMode="decimal"
                value={spotManualDisplay}
                onChange={handleSpotManualChange}
                placeholder="0,0000"
                className="bg-transparent border-[#FFB800]/50 font-mono-data text-lg focus-visible:ring-[#FFB800] max-w-[180px]"
              />
              {quote?.spot && (
                <button
                  type="button"
                  onClick={() => {
                    setSpotManualDisplay(quote.spot.toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 }));
                    setSpotManual(String(quote.spot));
                  }}
                  className="text-xs text-[#6b89a8] hover:text-[#FFB800] transition-colors border border-[#123a5e] hover:border-[#FFB800]/40 rounded-sm px-2 py-1"
                >
                  Usar spot ao vivo
                </button>
              )}
            </div>
            <p className="text-[11px] text-[#6b89a8]">
              Este valor será usado como taxa final da trava — sem spread ou juros adicionais.
            </p>
          </div>

          {/* Exibe a taxa bruta que será travada */}
          {spotManualNum > 0 && (
            <div className="border-t border-[#123a5e] pt-4">
              <p className="text-[10px] text-[#6b89a8] uppercase tracking-wider mb-1">Taxa final travada</p>
              <p className="text-5xl font-bold font-mono-data text-[#40B6A2]">
                {fmt(spotManualNum)}
              </p>
              {form.volume_usd && (
                <p className="text-xs text-[#6b89a8] font-mono-data mt-1">
                  Equivalente: R$ {fmt(spotManualNum * Number(form.volume_usd), 2)}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Aviso workflow */}
        <div className="flex items-start gap-2 text-xs text-[#FFB800] bg-[#FFB800]/5 border border-[#FFB800]/20 rounded-sm px-4 py-3">
          <Warning weight="fill" className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            Taxa aplicada diretamente como informada — sem spread.
          </span>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="border border-[#123a5e] rounded-sm p-6 bg-[#0B2D4D] space-y-5">
          <h2 className="text-lg font-semibold">Dados da operação</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Cliente */}
            <div className="space-y-2">
              <Label htmlFor="cliente" className="text-xs tracking-wider uppercase text-[#8aa9c9]">Cliente</Label>
              <ClienteSapInput
                key={clienteKey}
                value={clienteSap}
                manualId={cnpjIeConfirmed}
                onChange={handleClienteChange}
                allowManual={true}
              />
            </div>

            {/* Volume */}
            <div className="space-y-2">
              <Label htmlFor="volume" className="text-xs tracking-wider uppercase text-[#8aa9c9]">Volume (USD)</Label>
              <Input
                id="volume"
                data-testid="input-volume"
                type="text"
                inputMode="decimal"
                value={volumeDisplay}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/\./g, "").replace(/[^\d,]/g, "");
                  if (!cleaned) { setVolumeDisplay(""); setForm({ ...form, volume_usd: "" }); return; }
                  const comma = cleaned.indexOf(",");
                  if (comma === -1) {
                    const n = parseInt(cleaned, 10);
                    setVolumeDisplay(isNaN(n) ? "" : n.toLocaleString("pt-BR"));
                    setForm({ ...form, volume_usd: isNaN(n) ? "" : String(n) });
                  } else {
                    const intStr = cleaned.slice(0, comma);
                    const decStr = cleaned.slice(comma + 1).replace(/,/g, "");
                    const n = intStr ? parseInt(intStr, 10) : 0;
                    setVolumeDisplay(`${intStr ? n.toLocaleString("pt-BR") : "0"},${decStr}`);
                    setForm({ ...form, volume_usd: `${intStr || "0"}.${decStr}` });
                  }
                }}
                placeholder="0"
                className="bg-transparent border-[#123a5e] font-mono-data focus-visible:ring-[#40B6A2]"
              />
            </div>

            {/* Tipo de Vencimento */}
            <div className="space-y-3 md:col-span-2">
              <Label className="text-xs tracking-wider uppercase text-[#8aa9c9]">Tipo de Vencimento</Label>
              <div className="flex gap-5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="tipo_vencimento" checked={tipoVencimento === "data_fixa"} onChange={() => setTipoVencimento("data_fixa")} className="w-4 h-4 accent-[#40B6A2]" />
                  <span className="text-sm">Data fixa</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="tipo_vencimento" checked={tipoVencimento === "ddf"} onChange={() => setTipoVencimento("ddf")} className="w-4 h-4 accent-[#40B6A2]" />
                  <span className="text-sm">DDF</span>
                </label>
              </div>

              {tipoVencimento === "data_fixa" && (
                <div className="space-y-2 max-w-xs">
                  <Label htmlFor="dataVenc" className="text-xs tracking-wider uppercase text-[#8aa9c9]">Data de vencimento</Label>
                  <Input
                    id="dataVenc"
                    data-testid="input-data-venc"
                    type="date"
                    value={form.data_vencimento}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setForm({ ...form, data_vencimento: e.target.value })}
                    className="bg-transparent border-[#123a5e] font-mono-data focus-visible:ring-[#40B6A2]"
                  />
                </div>
              )}

              {tipoVencimento === "ddf" && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs tracking-wider uppercase text-[#8aa9c9]">Mês previsto de embarque</Label>
                      <select
                        value={ddfMes}
                        onChange={(e) => setDdfMes(e.target.value)}
                        className="w-full bg-[#061729] border border-[#123a5e] rounded-sm px-3 py-2 text-sm font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2]"
                      >
                        <option value="" className="bg-[#0B2D4D]">Selecione o mês</option>
                        {getMeses12().map(({ val, label }) => (
                          <option key={val} value={val} className="bg-[#0B2D4D]">{label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs tracking-wider uppercase text-[#8aa9c9]">Dias após faturamento (DDF)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={ddfDias}
                        onChange={(e) => setDdfDias(e.target.value)}
                        placeholder="Ex: 30"
                        className="bg-transparent border-[#123a5e] font-mono-data focus-visible:ring-[#40B6A2]"
                      />
                      <div className="flex gap-2">
                        {[30, 60, 90, 120].map((d) => (
                          <button key={d} type="button" onClick={() => setDdfDias(String(d))}
                            className={`px-2 py-1 text-xs border rounded-sm font-mono-data transition-colors ${ddfDias === String(d) ? "border-[#40B6A2] text-[#40B6A2] bg-[#40B6A2]/10" : "border-[#123a5e] text-[#6b89a8] hover:border-[#40B6A2]/50 hover:text-[#40B6A2]"}`}>
                            {d}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  {(() => {
                    const info = calcDdfInfo(ddfMes, ddfDias);
                    if (!info) return null;
                    return (
                      <div className="flex items-start gap-2 text-xs text-[#FFB800] bg-[#FFB800]/5 border border-[#FFB800]/20 rounded-sm px-3 py-2">
                        <Warning weight="fill" className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>Estimativa: pagamento em <strong>{info.mesPagLabel}</strong> · Prazo para referência: <strong>{info.prazo} dias</strong></span>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-[#123a5e] pt-5">
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <Button type="button" variant="outline" onClick={resetForm} data-testid="btn-limpar">
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading || !spotManualNum}
                data-testid="btn-enviar-aprovacao"
                className="bg-[#FFB800] text-black hover:bg-[#e6a600] font-semibold disabled:opacity-40"
              >
                {loading ? "Criando..." : <>Criar trava <CaretRight weight="bold" className="w-4 h-4 ml-2" /></>}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
}
